import {
  getAuthenticatedHomePath,
  getTokenAccessSurface,
  logout
} from '@core/utils'
import {
  Icon,
  LocaleSwitch,
  MenuPopup,
  NotificationPopup
} from '@components/molecules'
import { css } from '@emotion/react'
import { Avatar } from 'antd'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HeaderMenu, Flex } from './Header.styled'
import { useStore, useGetProfileImage, useFetch } from '@core/hooks'
import { Link, useHistory } from 'react-router-dom'
import { APIS } from '@constants/apis'
import { useEmployeeProfile } from '@core/providers/EmployeeProfileProvider'
import AdminAccountMenu from './AdminAccountMenu'

const HeaderContent = ({
  match,
  setDrawerOpen,
  userData,
  isAdmin = false,
  navigationToggle,
  headerForeground = '#ffffff',
  user,
  profileImage
}) => {
  const {
    t,
    i18n: { language }
  } = useTranslation()
  const history = useHistory()
  const [{ config }] = useStore()

  const injectI18n = React.useCallback(
    ({ title, ...rest }) => ({
      title: t(`common.${title}`),
      ...rest
    }),
    [t]
  )
  const handleMenuClicked = (id) => {
    switch (id) {
      case 'setting':
        return history.push(`/${match?.params?.companyGroup}/setting`)
      case 'signOut':
        return logout({ redirectWith: match?.params?.companyGroup })
      default:
    }
  }
  const renderName = React.useMemo(() => {
    if (language === 'th') {
      return `${user.firstName} ${user.lastName}`
    } else {
      return `${user.firstNameEN} ${user.lastNameEN}`
    }
  }, [language, user])

  return (
    <Flex
      justify="space-between"
      className={isAdmin ? 'admin-header' : undefined}
    >
      <HeaderMenu className="header-brand">
        {navigationToggle}
        {getTokenAccessSurface(userData) === 'main' ? (
          <Link to={getAuthenticatedHomePath(userData)}>
            <img
              src={
                config?.mainSetting?.logoUrl || '/general-outsoucing-logo.png'
              }
              alt="Logo"
              height={40}
            />
          </Link>
        ) : isAdmin ? (
          <span className="header-system-name">
            Employee Self Service (ESS)
          </span>
        ) : (
          'Employee Self Service (ESS)'
        )}

        {setDrawerOpen && (
          <Icon
            icon="Hamburger"
            color="#fff"
            size={30}
            onClick={() => setDrawerOpen(true)}
          />
        )}
      </HeaderMenu>

      <HeaderMenu className="header-account">
        {user.isSet && (
          <div
            className="header-user-name"
            title={isAdmin ? renderName : undefined}
            style={{ color: headerForeground }}
          >
            {renderName}
          </div>
        )}
        <LocaleSwitch
          className="header-locale"
          css={css`
            .locale-content {
              color: ${headerForeground};
              margin: 0 20px;
            }
          `}
        />
        {getTokenAccessSurface(userData) === 'main' && <NotificationPopup />}
        {isAdmin ? (
          <AdminAccountMenu
            name={user.isSet ? renderName : userData?._pis?.employeeCode}
            profileImage={profileImage}
            onMenuClicked={handleMenuClicked}
          />
        ) : (
          <MenuPopup
            overlayClassName="header-popup"
            menus={[
              { id: 'setting', title: 'setting', icon: 'Gear' },
              { id: 'signOut', title: 'sign-out', icon: 'Login' }
            ].map(injectI18n)}
            onMenuClicked={handleMenuClicked}
          >
            <Avatar
              // src={'/general-outsoucing-logo.png'}
              src={profileImage !== undefined ? profileImage : '/avatar.png'}
              style={{ border: '3px solid #e6e6e6', height: 45, width: 45 }}
            />
          </MenuPopup>
        )}
      </HeaderMenu>
    </Flex>
  )
}

// Keep the standalone loader for admin layouts, outside the employee provider.
const StandaloneHeader = (props) => {
  const { userData, match } = props
  const [user, setUser] = useState({ isSet: false })
  const { execute: _getUserInfo } = useFetch(APIS.USER.INFO)
  const { image: profileImage, getDownLoadImage: setProfileImage } =
    useGetProfileImage()
  const getProfileImage = useCallback(
    (profileImageUrl) => {
      setProfileImage({ profilePicture: profileImageUrl })
    },
    [setProfileImage]
  )

  useEffect(() => {
    if (userData?.profilePicture) {
      getProfileImage(userData.profilePicture)
    }
    _getUserInfo(
      { url: userData._pis.employeeCode },
      {
        onSuccess: ({ data }) => {
          setUser({
            firstName: data.pisFirstName,
            lastName: data.pisLastName,
            firstNameEN: data.pisFirstNameEN,
            lastNameEN: data.pisLastNameEN,
            jobPositionName: data.pisJobPositionName,
            jobPositionNameEN: data.pisJobPositionNameEN,
            email: data.pisEmail,
            employeeCode: userData._pis.employeeCode,
            groupModuleList: userData._pis.groupModuleList,
            isSet: true
          })
        }
      }
    )
  }, [getProfileImage, userData, match, setUser, _getUserInfo])

  return <HeaderContent {...props} user={user} profileImage={profileImage} />
}

const Header = (props) => {
  const profile = useEmployeeProfile()
  return profile ? (
    <HeaderContent
      {...props}
      user={profile.user}
      profileImage={profile.image}
    />
  ) : (
    <StandaloneHeader {...props} />
  )
}

export default Header
